import { useDomEvent } from 'hooks/use-dom-event'
import { Slot } from 'primitives/slot'
import * as React from 'react'
import { tw } from 'utils/classnames'

interface ScrollerProps extends React.ComponentProps<'div'>, AsChildProp {
  direction?: 'x' | 'y'
}

/**
 * This component helps you fix a very specific problem. I'll try my best to explain.
 *
 * We want to only show scrollbars when needed, this makes sense. So we use `overflow: auto`
 * in the scroller class. The scroller class helps customise the scrollbars so they look
 * like the design. We do this with CSS, however, it has one problem that the scrollbar
 * will always be "present", even when there is nothing to scroll. So it visibily still
 * takes up space.
 *
 * One thing with overflow auto is that it collapses the scrollbar when there
 * is nothing to scroll. So now we have a conflict. We need again to show the scrollbar
 * when there is something to scroll, but we also want to show the scrollbar when there
 * is nothing to scroll. To balance this all out, we do a trick in `scroller.css` to take
 * over the right padding and indent it with the scroller size. This balances out the
 * padding and makes it appear visually correct. However, when there is nothing to scroll
 * we cannot take over the padding, as it becomes unbalanced again.
 *
 * The solution here is:
 * - when you cannot scroll, show even padding (that is pr-[--scroller-gutter]).
 * - when you can scroll, show uneven padding (that is controlled in `scroller.css`
 *  so this component just "falls back" to the default that is set in scroller.css).
 *
 * @param {ScrollerProps} { direction, asChild, ...props }
 * @param {string} direction - x or y, use this to control which direction the scroller should be
 * @returns
 */
export function Scroller({ direction = 'x', asChild, ...props }: ScrollerProps) {
  const [canScroll, setCanScroll] = React.useState(false)
  const Component = asChild ? (Slot as unknown as 'div') : 'div'
  const ref = React.useRef<HTMLDivElement>(null)

  const onChange = React.useCallback(
    function onChange() {
      if (!ref.current) {
        return
      }

      // can element scroll?
      if (direction === 'y') {
        setCanScroll(ref.current.scrollWidth > ref.current.clientWidth)
      } else {
        setCanScroll(ref.current.scrollHeight > ref.current.clientHeight)
      }
    },
    [direction],
  )

  React.useEffect(() => {
    // listen for change and resize events to determine if we can scroll
    const resizeObserver = new ResizeObserver(onChange)
    const childObserver = new MutationObserver(onChange)

    if (ref.current) {
      resizeObserver.observe(ref.current)
      childObserver.observe(ref.current, { childList: true, subtree: true })
    }

    return () => {
      resizeObserver.disconnect()
      childObserver.disconnect()
    }
  }, [onChange])

  useDomEvent(useDomEvent.window, 'resize', onChange)

  const additionalProps: Record<string, boolean> = {}
  if (process.env.NODE_ENV === 'development') {
    additionalProps['data-can-scroll'] = canScroll
  }

  return (
    <Component
      ref={ref}
      {...props}
      {...additionalProps}
      className={tw(
        direction === 'x' ? 'scroller-xs' : 'scroller-y-xs',
        !canScroll && direction === 'x' ? 'pr-[--scroller-gutter]' : 'pl-[--scroller-gutter]',
        props.className,
      )}
    />
  )
}
